import React, { useEffect, useState, useCallback } from 'react';
import { Box, Card, Tab, Tabs } from '@mui/material';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { IConfirmation } from 'src/redux/confirmation/types';
import { OpenConfirmation } from 'src/redux/confirmation/confirmationReducer';
import { Helmet } from 'react-helmet-async';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { DataService } from 'src/services/data-service';
import { IFaq, IFaqListing } from '.';
import Loader from 'src/components/Loader';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddFaq from './add-faq';

type LanguageTab = 'en' | 'ar' | 'fr';

const FaqListing: React.FC = () => {
  const dispatch = useAppDispatch();

  const [allData, setAllData] = useState<IFaq[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [openAddFaq, setOpenAddFaq] = useState<boolean>(false);
  const [selectedFaq, setSelectedFaq] = useState<IFaq | null>(null);
  const [activeTab, setActiveTab] = useState<LanguageTab>('en');

  const activate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to activate this FAQ?',
      onSubmit: async () => {
        const result = await DataService.post('api/Faq/activate?id=' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'FAQ activated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to activate FAQ', type: 'error' }));
        }
      }
    };

    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const deactivate = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Do you want to deactivate this FAQ?',
      onSubmit: async () => {
        const result = await DataService.post(
          'api/Faq/deactivate?id=' + id,
          {}
        );
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'FAQ deactivated successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to deactivate FAQ', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const deleteFaq = useCallback((id: string) => {
    const confirmation: IConfirmation = {
      open: true,
      title: 'Confirmation',
      message: 'Are you sure you want to delete this FAQ? This action cannot be undone.',
      onSubmit: async () => {
        const result = await DataService.delete('api/Faq/delete/' + id, {});
        if (result.ok) {
          setRefreshKey(prev => prev + 1);
          dispatch(showAlert({ message: 'FAQ deleted successfully!', type: 'success' }));
        } else {
          dispatch(showAlert({ message: 'Failed to delete FAQ', type: 'error' }));
        }
      }
    };
    dispatch(OpenConfirmation(confirmation));
  }, [dispatch]);

  const editFaq = useCallback(async (id: string) => {
    try {
      const response = await DataService.get('api/Faq/get/' + id);
      if (response.ok) {
        const res = await response.json();
        const faq: IFaq = res.data;
        setSelectedFaq(faq);
        setOpenAddFaq(true);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch FAQ details', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading FAQ details', type: 'error' }));
    }
  }, [dispatch]);

  const headers = React.useMemo((): EnhancedTableColumn[] => [
    { id: 'title', label: 'Title', type: EnhancedTableColumnType.TEXT },
    { id: 'body', label: 'Body', type: EnhancedTableColumnType.TEXT },
    {
      id: 'status',
      label: 'Status',
      type: EnhancedTableColumnType.COLORED_CHIP,
      chipColors: {
        Active: { backgroundColor: 'green', color: 'white' },
        Inactive: { backgroundColor: 'red', color: 'white' }
      }
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        {
          onClick: editFaq,
          icon: <EditIcon fontSize="small" />,
          label: 'Edit FAQ'
        },
        {
          onClick: activate,
          icon: <CheckIcon fontSize="small" />,
          label: 'Activate FAQ',
          hidden: (r) => r.active === true
        },
        {
          onClick: deactivate,
          icon: <CancelIcon fontSize="small" />,
          label: 'Deactivate FAQ',
          hidden: (r) => r.active === false
        },
        {
          onClick: deleteFaq,
          icon: <DeleteIcon fontSize="small" />,
          label: 'Delete FAQ',
          color: 'error'
        }
      ]
    }
  ], [activate, deactivate, deleteFaq, editFaq]);

  const getFilteredData = useCallback((language: LanguageTab): Record<string, IFaqListing> => {
    const result: Record<string, IFaqListing> = {};

    allData
      .filter(f => f.language === language)
      .forEach((faq) => {
        result[faq.id] = {
          id: faq.id,
          title: faq.title,
          body: faq.body.length > 100 ? faq.body.substring(0, 100) + '...' : faq.body,
          language: faq.language,
          active: faq.active,
          status: faq.active ? 'Active' : 'Inactive'
        };
      });

    return result;
  }, [allData]);

  const fetchFaqs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await DataService.get('api/Faq/all');
      if (response.ok) {
        const res = await response.json();
        const list: IFaq[] = res.data;
        setAllData(list);
      } else {
        dispatch(showAlert({ message: 'Failed to fetch FAQs', type: 'error' }));
      }
    } catch (error) {
      dispatch(showAlert({ message: 'Error loading FAQs', type: 'error' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs, refreshKey]);

  const handleAddSuccess = useCallback(() => {
    setOpenAddFaq(false);
    setSelectedFaq(null);
    setRefreshKey(prev => prev + 1);
    dispatch(showAlert({
      message: selectedFaq ? 'FAQ updated successfully!' : 'FAQ added successfully!',
      type: 'success'
    }));
  }, [dispatch, selectedFaq]);

  const handleClose = useCallback(() => {
    setOpenAddFaq(false);
    setSelectedFaq(null);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: LanguageTab) => {
    setActiveTab(newValue);
  };

  const actions: ITableMenuAction[] = [];

  if (loading) {
    return (
      <Box>
        <Helmet>
          <title>FAQ List</title>
        </Helmet>
        <MainPageTitle title={'FAQs List'} />
        <Loader />
      </Box>
    );
  }

  const currentData = getFilteredData(activeTab);

  return (
    <div>
      <Helmet>
        <title>FAQ List</title>
      </Helmet>
      <MainPageTitle
        title={'FAQs List'}
        action={{
          title: 'Add FAQ',
          onClick: () => {
            setOpenAddFaq(true);
          }
        }}
      />

      <Card sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="faq language tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minWidth: 120,
              fontWeight: 600,
              fontSize: '0.95rem',
            },
            '& .Mui-selected': {
              color: '#fff',
            }
          }}
        >
          <Tab label="English" value="en" />
          <Tab label="العربية" value="ar" />
          <Tab label="Français" value="fr" />
        </Tabs>
      </Card>

      <EnhancedTable
        title={`FAQs (${activeTab.toUpperCase()})`}
        columns={headers}
        data={currentData}
        actions={actions}
      />
      {openAddFaq && (
        <AddFaq
          onSuccess={handleAddSuccess}
          onClose={handleClose}
          faq={selectedFaq}
        />
      )}
    </div>
  );
};

export default FaqListing;
